'use client';

import { useCallback, useRef } from 'react';
import {
  CellState,
  OPPOSITE_EDGE,
  clearHighlightAndEdges,
  deltaToEdgeDir,
  highlightColor,
  isHighlighted,
  setEdge,
  setHighlightColor,
  solidifyHighlightGroups,
} from '../highlight/cellState';
import { BoardPointer } from './pointer';
import { cellsBetween } from './cellPath';

/**
 * Pointer-interaction hook for highlight mode.
 *
 * `colorIndex`:
 *   0 → erase mode (always removes highlights)
 *   1–3 → paint colour. Acts as a toggle based on the cell the gesture starts on:
 *     starting on a cell that already has this colour runs an ERASE gesture; starting
 *     on an empty cell or one of a different colour runs an ADD gesture.
 *
 * The resolved gesture mode is captured into a ref at pointer-down so that switching
 * colours while a gesture is in progress doesn't produce mixed results. The right mouse
 * button always erases for the duration of the gesture, regardless of the selected colour.
 *
 * ADD gesture:
 *   - Pointer-down: paint the cell with `colorIndex` (erasing any different
 *     existing colour first, then connecting edges).
 *   - Pointer-move into an adjacent cell: same — erase any different colour,
 *     paint with `colorIndex`, connect edge to the previous cell.
 *   - A move that lands more than one cell away: every cell along the straight line between
 *     the two is painted, edges connecting as if each had been reported.
 *
 * ERASE gesture:
 *   - Pointer-down / move: clear the highlight + all edge bits on the cell
 *     and remove reciprocal edges from its neighbours.
 *
 * Board dimensions (which may be rectangular) are derived from the `cells`
 * arrays passed to the handlers.
 *
 * `canHighlight` lets a game mark certain cells as non-highlightable (e.g. fixed
 * clue cells). Those cells are skipped when painting/erasing, and a gesture that
 * passes over one does not connect an edge across it. Defaults to all cells
 * being highlightable.
 */
export const useHighlightGesture = (
  setCells: (cells: CellState[][]) => void,
  colorIndex: number,
  canHighlight: (cell: CellState) => boolean = () => true
) => {
  const gestureActive = useRef(false);
  /** Colour index captured at pointer-down — fixed for the whole gesture. */
  const gestureColorIndex = useRef(0);
  const lastCell = useRef<[number, number] | null>(null);
  /**
   * Where the pointer was last seen, which is not the same as `lastCell`: erase mode never sets
   * that, and a move onto a non-highlightable cell deliberately leaves it alone. Filling the gap
   * between samples needs the pointer's own trail.
   */
  const lastPointerCell = useRef<[number, number] | null>(null);
  const pendingCells = useRef<CellState[][] | null>(null);
  const visitedRef = useRef(new Set<string>());

  const setCellsRef = useRef(setCells);
  setCellsRef.current = setCells;
  const canHighlightRef = useRef(canHighlight);
  canHighlightRef.current = canHighlight;

  /** Apply the gesture colour to a cell, erasing any different existing colour first. */
  function paintCell(newCells: CellState[][], row: number, col: number, gColorIndex: number): void {
    const existing = highlightColor(newCells[row][col]);
    if (existing !== 0 && existing !== gColorIndex) {
      // Different colour already here — erase it (clears edges on this cell
      // and removes reciprocal edges from neighbours) before painting.
      clearHighlightAndEdges(newCells, row, col);
    }
    newCells[row][col] = setHighlightColor(newCells[row][col], gColorIndex);
  }

  const handleStart = useCallback(
    (pointer: BoardPointer, cells: CellState[][]) => {
      const { row, col } = pointer;

      // Determine the gesture mode from the start cell. The right mouse button always
      // erases. Otherwise a selected paint colour acts as a toggle: starting on a cell that
      // already has the selected colour begins an erase gesture (clearing every cell dragged
      // over, regardless of its colour); starting anywhere else paints the selected colour.
      const startColor = highlightColor(cells[row][col]);
      const gci = pointer.secondary || (colorIndex !== 0 && startColor === colorIndex) ? 0 : colorIndex;
      gestureColorIndex.current = gci;
      gestureActive.current = true;
      lastPointerCell.current = [row, col];
      visitedRef.current = new Set([`${row},${col}`]);

      const newCells = cells.map((r) => [...r]);
      const allowed = canHighlightRef.current(newCells[row][col]);

      if (gci === 0) {
        // Erase mode
        if (allowed) clearHighlightAndEdges(newCells, row, col);
        lastCell.current = null;
      } else if (allowed) {
        // Add mode
        paintCell(newCells, row, col, gci);
        lastCell.current = [row, col];
      } else {
        // Non-highlightable cell (e.g. a clue cell): start the gesture but skip it.
        lastCell.current = null;
      }

      pendingCells.current = newCells;
      setCellsRef.current(newCells);
    },
    [colorIndex]
  );

  const handleEnd = useCallback(() => {
    // After an add gesture, fully connect every highlight group so a rectangle
    // traced as a loop/U/snake survives deletion of any single cell. Deferred
    // to gesture end so it doesn't slow down per-cell painting.
    if (gestureActive.current && gestureColorIndex.current !== 0 && pendingCells.current) {
      const newCells = pendingCells.current.map((r) => [...r]);
      if (solidifyHighlightGroups(newCells)) setCellsRef.current(newCells);
    }
    gestureActive.current = false;
    lastCell.current = null;
    lastPointerCell.current = null;
    pendingCells.current = null;
    visitedRef.current = new Set();
  }, []);

  const handleMove = useCallback((pointer: BoardPointer, cells: CellState[][]) => {
    if (!gestureActive.current) return;

    const gci = gestureColorIndex.current; // use gesture-start colour
    const effective = pendingCells.current ?? cells;

    /*
      Every cell between the last sample and this one, not just the one that arrived. Two samples a
      few cells apart mean a fast drag, not a jump, and painting only the endpoint leaves the run
      dotted — visibly so on native, where move events are coalesced when the JS thread is busy.

      The whole path is applied to one copy and published once, so a gap costs a single render
      rather than one per cell.
    */
    const from = lastPointerCell.current;
    const to: [number, number] = [pointer.row, pointer.col];
    const path = from ? cellsBetween(from, to) : [to];
    lastPointerCell.current = to;
    if (path.length === 0) return;

    let newCells: CellState[][] | null = null;
    const current = () => newCells ?? effective;
    const mutable = () => (newCells ??= effective.map((r) => [...r]));

    for (const [row, col] of path) {
      // Skip non-highlightable cells; leave lastCell untouched so no edge is
      // connected across the gap to the next highlightable cell.
      if (!canHighlightRef.current(current()[row][col])) continue;

      if (gci === 0) {
        // Erase mode
        const key = `${row},${col}`;
        if (visitedRef.current.has(key)) continue;
        if (!isHighlighted(current()[row][col])) continue;
        visitedRef.current.add(key);

        clearHighlightAndEdges(mutable(), row, col);
      } else {
        // Add mode
        const last = lastCell.current;
        if (last && last[0] === row && last[1] === col) continue;

        const target = mutable();

        // Paint (erases any different existing colour first)
        paintCell(target, row, col, gci);

        // Connect an edge to the previous cell if directly adjacent
        if (last) {
          const [lr, lc] = last;
          const dir = deltaToEdgeDir(row - lr, col - lc);
          if (dir !== null && highlightColor(target[lr][lc]) === gci) {
            target[lr][lc] = setEdge(target[lr][lc], dir, true);
            target[row][col] = setEdge(target[row][col], OPPOSITE_EDGE[dir], true);
          }
        }

        lastCell.current = [row, col];
      }
    }

    if (newCells) {
      pendingCells.current = newCells;
      setCellsRef.current(newCells);
    }
  }, []);

  /** Single entry point: a gesture surface feeds every phase of the gesture through here. */
  const handleBoardPointer = useCallback(
    (pointer: BoardPointer, cells: CellState[][]) => {
      if (pointer.kind === 'start') handleStart(pointer, cells);
      else if (pointer.kind === 'move') handleMove(pointer, cells);
      else handleEnd();
    },
    [handleStart, handleMove, handleEnd]
  );

  return { handleBoardPointer };
};
